import { inject } from '@angular/core';
import { Route, Router, UrlSegment } from '@angular/router';
import { PARTNER_REGISTRATION_MODE, PartnerDbModel } from '@db-models/partner-db.model';
import { AuthService } from '@feature-services/auth.service';
import { PartnerService } from '@feature-services/partner.service';
import { AlertToastrService } from '@util-services/alert-toastr.service';
import { HelperService } from '@util-services/helper.service';
import { map, Observable, of } from 'rxjs';

/**
 * OnboardGuard - Unified guard for partner onboarding flows
 *
 * Handles these scenarios:
 * 1. Legacy partners (is_setup_done === 1): Full dashboard access
 * 2. New unapproved PLG partner (is_verified_by_support === false, is_setup_done === 0):
 *    held on /account-under-review until the Calenso team verifies the account
 *    (SLG waits on /almost-there, where the profile + sales call are completed)
 * 3. New sign-up flow (approved): controls access to /almost-there by approval status
 * 4. Old wizard flow (is_setup_done === 0): Redirects to /onboard wizard
 *
 * Flow priority:
 * - Legacy partner → dashboard access, redirect away from waiting/wizard pages
 * - New flow approved → dashboard access, redirect away from /almost-there
 * - New flow unapproved → redirect to /almost-there
 * - Old wizard flow → redirect to /onboard with current step
 *
 * Performance optimization:
 * - If is_setup_done === 1 in local storage, uses cached data (no API call)
 * - Otherwise fetches fresh data to check approval/onboarding status
 */
export const OnboardGuard = (_route: Route, segments: UrlSegment[]): Observable<boolean> => {
  const partnerService = inject(PartnerService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const alertToastrService = inject(AlertToastrService);
  const helperService = inject(HelperService);

  const urlPath = helperService.getUrlPathFromSegments(segments);
  const isAlmostTherePage = urlPath?.includes('almost-there');
  const isOnboardPage = urlPath?.includes('onboard');
  const isAccountReviewPage = urlPath?.includes('account-under-review');

  const cachedPartner = partnerService.getPartnerFromLocalStorage();

  if (cachedPartner?.is_setup_done === 1) {
    if (isAlmostTherePage || isOnboardPage || isAccountReviewPage) {
      router.navigate(['/']);
      return of(false);
    }
    return of(true);
  }

  const partnerUuid = partnerService.getPartnerUuid();

  if (!partnerUuid) {
    const redirectionUrl = new URL(window.location.href);
    const target = `${redirectionUrl.pathname}${redirectionUrl.search}`;
    authService.logout({ 'login-required': 'true', redirect: target });
    return of(false);
  }

  return partnerService.getPartner(partnerUuid, true).pipe(
    map((partner: PartnerDbModel) => {
      const isApproved = partner?.is_onboarding_call_completed === true && partner?.is_verified_by_support === true;
      const isPLG = partner?.registration_mode === PARTNER_REGISTRATION_MODE.PLG
        || partner?.registration_mode === undefined;

      // New PLG partner not yet approved by the Calenso team (and no setup done) →
      // hold on the account-under-review screen until support verifies them.
      // SLG is excluded on purpose: those partners wait on /almost-there, where they
      // complete their profile and the sales call before support approves them.
      if (isPLG && partner?.is_verified_by_support === false && partner?.is_setup_done === 0) {
        if (isAccountReviewPage) {
          return true;
        }
        router.navigate(['/account-under-review']);
        return false;
      }

      if (partner?.is_setup_done === 1) {
        if (isAlmostTherePage || isOnboardPage || isAccountReviewPage) {
          router.navigate(['/']);
          return false;
        }
        return true;
      } else if (!isApproved) {
        const salesCallPending = !!partner?.sales_intro_call_required && partner?.is_onboarding_call_completed !== true;

        // PLG partners who have started onboarding bypass almost-there
        if (isPLG && !salesCallPending && partner?.current_setup_step >= 1) {
          if (isOnboardPage) {return true;}
          router.navigate(['/onboard'], {
            queryParams: { step: partner.current_setup_step + 1 }
          });
          return false;
        }

        // Allow PLG partners to access /onboard directly (skip call)
        if (isPLG && !salesCallPending && isOnboardPage) {
          return true;
        }

        if (isAlmostTherePage) {
          return true;
        }
        router.navigate(['/almost-there']);
        return false;
      } else if (partner?.is_setup_done === 0) {
        if (isOnboardPage) {
          return true;
        }
        router.navigate(['/onboard'], {
          queryParams: { step: partner.current_setup_step + 1 }
        }).then(() => {
          if (partner.current_setup_step > 1) {
            alertToastrService.showSuccess('onboard.resume_process_message');
          }
        });
        return false;
      }
      return true;
    })
  );
};
